import { NgModule, APP_INITIALIZER, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { PreloadAllModules, RouteReuseStrategy, RouterModule } from '@angular/router';
import { CurrencyPipe, PercentPipe, TitleCasePipe, UpperCasePipe } from '@angular/common';
import { ServiceWorkerModule } from '@angular/service-worker';
import { IonicModule, IonicRouteStrategy } from '@ionic/angular';
import { NbChatModule, NbChatOptions, NbThemeModule } from '@nebular/theme';

import { AppComponent } from './app.component';

// https://github.com/stripe/stripe-js#import-as-a-side-effect
import '@stripe/stripe-js';

// services
import {
    AAFlipService,
    AARentalService,
    AlertService,
    AppConfigService,
    AuthService,
    AutocompleteService,
    CalculatorService,
    CsvImportService,
    CustomerService,
    DatasourceResolverService,
    FileService,
    ForceUpgradeService,
    HotsheetService,
    ModalService,
    OfferFlipCalculatorService,
    OfferRentalCalculatorService,
    PropertyAnalysisService,
    PropertyService,
    ToastService,
    UserService,
    UtilsService,
    FlipAnalysisService,
    RentalAnalysisService,
    OfferService,
    DocumentService,
    EventLogService,
    ErrorHandlerService,
    ContractTemplateService,
    VendorService,
    PrintService,
    SettingsService,
    OrganizationService,
    CustomerImportService,
    ContractService,
    ChatService
} from './services';

import { PropertyStateService } from './state/property-state.service';
import { CustomerStateService } from './state/customer-state.service';
import { VendorStateService } from './state/vendor-state.service';

import { SharedModule } from './shared.module';
import * as Parse from 'parse';
import { NbEvaIconsModule } from '@nebular/eva-icons';
import { ChatSidemenuModule } from './components/chat-sidemenu/chat-sidemenu.component.module';
import { RoutingService } from './services/routing.service';
import { PlatformService } from './services/platform.service';
import { HttpClientModule } from '@angular/common/http';

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        IonicModule.forRoot(),
        // Empty root route set; RoutingService.initRouting() loads the web (AppRoutingModule)
        // or mobile (MobileRoutingModule) route config at runtime based on platform.
        RouterModule.forRoot([], { preloadingStrategy: PreloadAllModules }),
        SharedModule,
        ServiceWorkerModule.register('ngsw-worker.js', {
          enabled: !isDevMode(),
          // Register the ServiceWorker as soon as the application is stable
          // or after 30 seconds (whichever comes first).
          registrationStrategy: 'registerWhenStable:30000'
        }),
        NbEvaIconsModule,
        ChatSidemenuModule,
        HttpClientModule
    ],
    providers: [
        // Ionic providers
        CurrencyPipe,  // used in PropertyAnalysisService
        PercentPipe,   // used in PropertyAnalysisService
        TitleCasePipe, // used in PropertyStateService
        UpperCasePipe, // used in PropertyStateService
        NbChatOptions,
        {
            provide: APP_INITIALIZER,
            useFactory: appInitFactory,
            deps: [
                AppConfigService,
                SettingsService,
                AuthService,
                AutocompleteService,
                ErrorHandlerService
            ],
            multi: true
        },
        RoutingService,
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },

        // app providers
        AppConfigService,
        PropertyService,
        PropertyAnalysisService,
        AuthService,
        UserService,
        ToastService,
        CustomerService,
        CalculatorService,
        AutocompleteService,
        UtilsService,
        FileService,
        ForceUpgradeService,
        ModalService,
        AlertService,
        HotsheetService,
        CsvImportService,
        OfferService,
        DatasourceResolverService,
        OfferRentalCalculatorService,
        OfferFlipCalculatorService,
        AARentalService,
        AAFlipService,
        FlipAnalysisService,
        RentalAnalysisService,
        DocumentService,
        EventLogService,
        ErrorHandlerService,
        ContractService,
        ContractTemplateService,
        VendorService,
        PrintService,
        SettingsService,
        OrganizationService,
        CustomerImportService,
        ChatService,
        PlatformService,

        // state
        PropertyStateService,
        CustomerStateService,
        VendorStateService
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}

function appInitFactory(
    config: AppConfigService,
    settings: SettingsService,
    auth: AuthService,
    autocomplete: AutocompleteService,
    errorHandler: ErrorHandlerService
) {
    return () => {
        return new Promise(async (resolve) => {
            (Parse as any).serverURL = config.server.url;
            Parse.initialize(config.server.appId);
            console.log(`Parse server: ${config.server.url}`);

            if (Parse.User.current()) {
                try {
                    await Parse.User.current()?.fetchWithInclude(['organization']);
                } catch (error) {
                    console.error(error);
                    errorHandler.handleInvalidSessionError(error);
                }
            }

            auth.authenticate();
            auth.currentUserChanged.subscribe(auth.authenticate);

            document.title = `${config.server.title} - Powered by Vesta`;
            settings.updateAppColors();
            void config.fetchParseConfig();

            // Load Google Places Library
            await autocomplete.loadScript();

            resolve(null);
        });
    };
}
